import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import api, { dataAPI } from '../services/api';
import useVaultItems from '../hooks/useVaultItems';
import { encryptForShare, hashSharePassword } from '../services/shareCrypto';
import { Card, Input, PillButton, Chip, Toggle, SegmentedControl, EmptyState, Modal, Toast } from '../ui';
import { ReactComponent as ShareIcon } from '../assets/icons/share.svg';
import { ReactComponent as TrashIcon } from '../assets/icons/trash.svg';
import { ReactComponent as SearchIcon } from '../assets/icons/search.svg';
import { ReactComponent as PlusIcon } from '../assets/icons/plus.svg';
import { ReactComponent as CopyIcon } from '../assets/icons/copy.svg';
import { ReactComponent as LockIcon } from '../assets/icons/lock.svg';
import './Share.css';
import { apiErrorMessage } from '../utils/apiError';

const USE_OPTIONS = [1, 3, 10];
const EXPIRY_OPTIONS = [
  { value: 60, key: '1h' },
  { value: 1440, key: '24h' },
  { value: 10080, key: '7d' },
  { value: 43200, key: '30d' }
];

// Campos ofrecidos para incluir en el enlace, por tipo de ítem. Un login solo
// enseña lo que suele hacer falta compartir; el resto se queda fuera salvo
// que el usuario lo marque explícitamente.
const FIELD_KEYS = ['username', 'password', 'url', 'notes', 'email'];

function relativeExpiry(expiresAt, t) {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return t('sharePage.expired');
  const hours = Math.round(ms / 3600000);
  if (hours < 24) return t('sharePage.expiresInHours', { count: hours });
  return t('sharePage.expiresInDays', { count: Math.round(hours / 24) });
}

/**
 * Modal de creación de un enlace compartido. Se exporta aparte para que el
 * detalle de un ítem del vault pueda abrirlo directamente sobre un item ya
 * conocido, sin pasar por el selector de esta pantalla.
 */
export function ShareSheet({ open, onClose, onCreated, initialItem }) {
  const { t } = useTranslation();
  const { items } = useVaultItems({ limit: 200 });
  const rfidId = localStorage.getItem('rfidId');

  const [search, setSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState(initialItem || null);
  const [fields, setFields] = useState({ username: true, password: true, url: true, notes: false, email: false });
  const [maxUses, setMaxUses] = useState(1);
  const [expiresInMinutes, setExpiresInMinutes] = useState(1440);
  const [password, setPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { url, expiresAt }

  useEffect(() => { if (open) { setSelectedItem(initialItem || null); setResult(null); setError(''); } }, [open, initialItem]);

  const filteredItems = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return items;
    return items.filter((i) => i.title.toLowerCase().includes(q) || (i.username || '').toLowerCase().includes(q));
  }, [items, search]);

  const canCreate = !!selectedItem;

  const create = useCallback(async () => {
    if (!selectedItem) return;
    setCreating(true);
    setError('');
    try {
      const detail = await dataAPI.get(selectedItem.id, rfidId);
      const full = typeof detail.data?.data === 'string' ? JSON.parse(detail.data.data) : (detail.data?.data || {});
      const payload = {};
      FIELD_KEYS.forEach((k) => { if (fields[k] && full[k] !== undefined) payload[k] = full[k]; });

      const { ciphertext, iv, keyBase64url } = await encryptForShare(payload);

      const res = await api.post('/share', {
        itemId: selectedItem.id,
        itemName: selectedItem.title,
        ciphertext,
        iv,
        maxUses,
        expiresInMinutes,
        passwordHash: password ? await hashSharePassword(password) : undefined
      });

      const url = `${res.data.url}#${keyBase64url}`;
      setResult({ url, expiresAt: res.data.expiresAt });
      onCreated?.();
    } catch (e) {
      setError(apiErrorMessage(e, t));
    } finally {
      setCreating(false);
    }
  }, [selectedItem, fields, maxUses, expiresInMinutes, password, rfidId, onCreated, t]);

  const [qrDataUrl, setQrDataUrl] = useState('');
  useEffect(() => {
    if (result?.url) QRCode.toDataURL(result.url, { margin: 1, width: 200 }).then(setQrDataUrl).catch(() => setQrDataUrl(''));
  }, [result]);

  return (
    <Modal open={open} onClose={onClose} title={t('sharePage.newLink')}>
      {result ? (
        <div className="sgc-share-sheet__result">
          {qrDataUrl && <img src={qrDataUrl} alt={t('common.qrCode')} className="sgc-share-sheet__qr" />}
          <Input
            readOnly
            value={result.url}
            rightAction={(
              <button type="button" onClick={() => navigator.clipboard.writeText(result.url)} title={t('common.actions')}>
                <CopyIcon width={16} height={16} aria-hidden="true" />
              </button>
            )}
          />
          <p className="sgc-share-sheet__hint">{t('sharePage.linkCreatedHint')}</p>
          <PillButton onClick={onClose}>{t('common.close')}</PillButton>
        </div>
      ) : (
        <div className="sgc-share-sheet">
          {!selectedItem ? (
            <>
              <Input icon={SearchIcon} placeholder={t('sharePage.searchItem')} value={search} onChange={(e) => setSearch(e.target.value)} />
              <ul className="sgc-share-sheet__item-list">
                {filteredItems.map((i) => (
                  <li key={i.id}>
                    <button type="button" onClick={() => setSelectedItem(i)}>
                      <span>{i.title}</span>
                      <span className="sgc-share-sheet__item-user">{i.username || '—'}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <div className="sgc-share-sheet__selected">
                <span>{selectedItem.title}</span>
                {!initialItem && (
                  <button type="button" onClick={() => setSelectedItem(null)}>{t('common.edit')}</button>
                )}
              </div>

              <p className="sgc-share-sheet__label">{t('sharePage.fieldsToInclude')}</p>
              <div className="sgc-share-sheet__fields">
                {FIELD_KEYS.map((k) => (
                  <Toggle key={k} label={t(`sharePage.field.${k}`)} checked={fields[k]} onChange={(v) => setFields((f) => ({ ...f, [k]: v }))} />
                ))}
              </div>

              <p className="sgc-share-sheet__label">{t('sharePage.maxUses')}</p>
              <SegmentedControl
                options={USE_OPTIONS.map((v) => ({ value: v, label: String(v) }))}
                value={maxUses}
                onChange={setMaxUses}
              />

              <p className="sgc-share-sheet__label">{t('sharePage.expiry')}</p>
              <SegmentedControl
                options={EXPIRY_OPTIONS.map((o) => ({ value: o.value, label: t(`sharePage.expiryOptions.${o.key}`) }))}
                value={expiresInMinutes}
                onChange={setExpiresInMinutes}
              />

              <Input
                icon={LockIcon}
                type="password"
                label={t('sharePage.optionalPassword')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              {error && <p className="sgc-input__error">{error}</p>}

              <PillButton disabled={!canCreate || creating} loading={creating} onClick={create}>
                {t('sharePage.createLink')}
              </PillButton>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

function Share() {
  const { t } = useTranslation();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSheet, setShowSheet] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/share/mine').then((res) => setLinks(res.data?.links || [])).catch(() => setLinks([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = useCallback(async (id) => {
    if (!window.confirm(t('sharePage.confirmRevoke'))) return;
    try {
      await api.delete(`/share/${id}`);
      setLinks((prev) => prev.map((l) => (l.id === id ? { ...l, revoked: true } : l)));
    } catch {
      setToast({ type: 'error', message: t('sharePage.revokeError') });
    }
  }, [t]);

  const statusFor = (link) => {
    if (link.revoked) return t('sharePage.status.revoked');
    if (new Date(link.expiresAt) < new Date()) return t('sharePage.status.expired');
    if (link.uses >= link.maxUses) return t('sharePage.status.exhausted');
    return t('sharePage.status.active');
  };

  return (
    <div className="sgc-share-page">
      <div className="sgc-share-page__header">
        <h1 className="sgc-share-page__title">{t('sharePage.title')}</h1>
        <PillButton icon={PlusIcon} size="sm" onClick={() => setShowSheet(true)}>{t('sharePage.newLink')}</PillButton>
      </div>

      {loading ? (
        <p className="sgc-share-page__loading">{t('common.loading')}</p>
      ) : links.length === 0 ? (
        <EmptyState
          icon={ShareIcon}
          title={t('sharePage.emptyTitle')}
          description={t('sharePage.emptyDescription')}
          actionLabel={t('sharePage.newLink')}
          onAction={() => setShowSheet(true)}
        />
      ) : (
        <Card padded={false}>
          <ul className="sgc-share-page__list">
            {links.map((link) => (
              <li key={link.id} className="sgc-share-page__row">
                <div className="sgc-share-page__info">
                  <p className="sgc-share-page__name">{link.itemName}</p>
                  <p className="sgc-share-page__meta">
                    {link.uses}/{link.maxUses} · {relativeExpiry(link.expiresAt, t)}
                  </p>
                </div>
                <Chip label={statusFor(link)} selected={statusFor(link) === t('sharePage.status.active')} />
                {!link.revoked && (
                  <button type="button" className="sgc-share-page__revoke" onClick={() => revoke(link.id)} title={t('sharePage.revoke')}>
                    <TrashIcon width={16} height={16} aria-hidden="true" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <ShareSheet open={showSheet} onClose={() => setShowSheet(false)} onCreated={load} />

      {toast && <div className="sgc-share-page__toast"><Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} /></div>}
    </div>
  );
}

export default Share;
